// src/components/ProductosCarousel.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Carousel,
    Row,
    Col,
    Card,
    Button,
    Image,
    Modal,
    Badge,
} from "react-bootstrap";

/* Helpers */
function chunk(arr, size) {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
}
function getPerSlide() {
    if (typeof window === "undefined") return 1;
    const w = window.innerWidth;
    if (w >= 992) return 3;
    if (w >= 768) return 2;
    return 1;
}
const toArray = (val) => (val == null ? [] : Array.isArray(val) ? val : [val]);

/* Subcomponentes del modal */
function SectionLabel({ children }) {
    if (!children) return null;
    return <div className="small text-muted fw-semibold mb-1">{children}</div>;
}
function PillList({ label, items, suffix }) {
    const arr = toArray(items);
    if (arr.length === 0) return null;
    return (
        <div className="mb-2">
            {label && <SectionLabel>{label}</SectionLabel>}
            <div className="producto-badges">
                {arr.map((x, i) => (
                    <Badge key={i} bg="light" text="dark" className="border">
                        {typeof x === "number" ? `${x}${suffix ?? ""}` : x}
                    </Badge>
                ))}
            </div>
        </div>
    );
}
function BulletedList({ label, items }) {
    const arr = toArray(items);
    if (arr.length === 0) return null;
    return (
        <div className="mb-2">
            {label && <SectionLabel>{label}</SectionLabel>}
            <ul className="producto-lista mb-0">
                {arr.map((x, i) => (
                    <li key={i}>{x}</li>
                ))}
            </ul>
        </div>
    );
}

/* Modal de detalle */
function ProductoModal({ show, onHide, producto }) {
    if (!producto) return null;
    const {
        icono,
        titulo,
        subtitulo,
        descripcion,
        tipoMoneda,
        plazos,
        tasaInteres,
        garantias,
        tipo,
        ventajas,
        destino,
        nota,
    } = producto;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            dialogClassName="facach-modal"
            contentClassName="facach-modal-content"
            backdropClassName="facach-modal-backdrop"
        >
            <div className="facach-modal-hero" style={{ background: "linear-gradient(135deg,#252A58,#1e234a)" }}>
                <div className="facach-modal-hero-overlay" />
                <div className="facach-modal-hero-content d-flex align-items-center gap-2">
                    {icono && <Image src={icono} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />}
                    <div>
                        <div className="facach-chip">Producto</div>
                        <h4 className="facach-modal-title mb-0">{titulo}</h4>
                        {subtitulo && <div className="small" style={{ opacity: 0.9 }}>{subtitulo}</div>}
                    </div>
                </div>
                <button type="button" className="btn facach-close" aria-label="Cerrar" onClick={onHide}>✕</button>
            </div>

            <div className="facach-modal-body">
                {descripcion && <p className="facach-parrafo">{descripcion}</p>}
                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <PillList label="Moneda" items={tipoMoneda} />
                        <PillList label="Plazos" items={plazos} suffix=" días" />
                        <PillList label="Tasa de interés" items={tasaInteres} />
                    </div>
                    <div className="col-12 col-md-6">
                        <PillList label="Garantías" items={garantias} />
                        <PillList label="Tipo" items={tipo} />
                        <PillList label="Ventajas" items={ventajas} />
                    </div>
                </div>
                <BulletedList label="Destino" items={destino} />
                {nota && <div className="producto-nota">{nota}</div>}
            </div>
        </Modal>
    );
}

/* Carrusel */
export default function ProductosCarousel({
    items = [],
    showTitle = false, // <- por defecto SIN título
    title = "Productos",
    interval = 8000,
    indicators = true,
    controls = true,
    descriptionMode = "full", // "full" | "clamp" | "scroll"
}) {
    const [perSlide, setPerSlide] = useState(getPerSlide());
    const [activeIndex, setActiveIndex] = useState(0);

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const openModal = (p) => { setSelected(p); setShowModal(true); };
    const closeModal = () => { setShowModal(false); setSelected(null); };

    useEffect(() => {
        const onResize = () => setPerSlide(getPerSlide());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => setActiveIndex(0), [items, perSlide]);

    const slides = useMemo(() => chunk(items, perSlide), [items, perSlide]);
    if (!items || items.length === 0) return null;

    // Clase para el modo de descripción
    const descClass =
        descriptionMode === "clamp" ? "producto-descripcion producto-descripcion--clamp" :
            descriptionMode === "scroll" ? "producto-descripcion producto-descripcion--scroll" :
                "producto-descripcion";

    return (
        <section className="productos-carousel">
            {/* Ya no mostramos el título por defecto */}
            {showTitle && <h5 className="mb-3">{title}</h5>}

            <Carousel
                activeIndex={activeIndex}
                onSelect={(i) => setActiveIndex(i)}
                interval={6000}          // auto-advance cada 6s (ajústalo)
                indicators={true}        // o false si no quieres los puntitos
                controls={false}         // <- sin flechas
                keyboard
                touch
                pause={false}            // <- no se detiene al hover
                wrap={true}              // <- loop continuo
                className="productos-carousel__inner"
            >
                {slides.map((group, idx) => (
                    <Carousel.Item key={`slide-${idx}`}>
                        <Row className="g-3">
                            {group.map((p, j) => (
                                <Col key={`card-${idx}-${j}`} xs={12} md={6} lg={4}>
                                    <Card className="h-100 shadow-sm servicio-card">
                                        <Card.Body className="producto-card-body">
                                            {p.icono && (
                                                <div className="mb-2 d-flex justify-content-center">
                                                    <Image src={p.icono} alt="" className="rounded" style={{ width: 56, height: 56, objectFit: "contain" }} />
                                                </div>
                                            )}

                                            {p.titulo && <Card.Title className="text-center mb-1">{p.titulo}</Card.Title>}
                                            {p.subtitulo && <div className="text-center text-muted small mb-2">{p.subtitulo}</div>}

                                            {p.descripcion && <Card.Text className={descClass}>{p.descripcion}</Card.Text>}

                                            {/* Enlace "Ver más" */}
                                            <Button
                                                variant="link"
                                                className="producto-vermas-link mt-auto p-0"
                                                onClick={() => openModal(p)}
                                            >
                                                Ver más
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Carousel.Item>
                ))}
            </Carousel>

            <ProductoModal show={showModal} onHide={closeModal} producto={selected} />
        </section>
    );
}
